<template>
  <div
    class="static-service-map"
    :class="{ 'static-service-map--immersive': isImmersive }"
  >
    <div class="static-service-map__frame">
      <div
        ref="viewportRef"
        class="static-service-map__viewport"
        :class="{
          'static-service-map__viewport--pannable': isPannable,
          'static-service-map__viewport--dragging': isDragging
        }"
        :style="viewportStyle"
        @click="handleViewportClick"
        @pointerdown="handlePointerDown"
        @pointermove="handlePointerMove"
        @pointerup="handlePointerUp"
        @pointercancel="handlePointerUp"
        @wheel.prevent="handleWheel"
      >
        <div class="static-service-map__pan" :style="panStyle">
          <div class="static-service-map__scene" :style="sceneStyle">
            <img
              class="static-service-map__image"
              src="/images/map/apac-service-network.svg"
              :alt="alt || $t('common.mapOverview')"
              draggable="false"
            >

            <svg
              v-if="isImmersive"
              class="static-service-map__depth-grid"
              :viewBox="mapViewBox"
              aria-hidden="true"
              focusable="false"
              preserveAspectRatio="none"
            >
              <path
                v-for="line in depthGridLines"
                :key="line"
                :d="line"
              />
            </svg>

            <svg
              v-if="serviceArcs.length"
              class="static-service-map__service-arcs"
              :viewBox="mapViewBox"
              aria-hidden="true"
              focusable="false"
              preserveAspectRatio="none"
            >
              <path
                v-for="arc in serviceArcs"
                :key="arc.id"
                class="static-service-map__service-arc"
                :class="{ 'static-service-map__service-arc--planned': arc.isComingSoon }"
                :d="arc.path"
              />
            </svg>

            <svg
              v-if="countryLabels.length"
              class="static-service-map__country-connectors"
              :viewBox="mapViewBox"
              aria-hidden="true"
              focusable="false"
              preserveAspectRatio="none"
            >
              <g
                v-for="country in countryLabels"
                :key="`${country.id}-connector`"
                class="static-service-map__country-connector"
                :class="{ 'static-service-map__country-connector--planned': country.isComingSoon }"
              >
                <path :d="getCountryConnectorPath(country)" />
                <circle
                  class="static-service-map__country-connector-ring"
                  :cx="country.xPx"
                  :cy="country.yPx"
                  r="12"
                />
                <circle
                  class="static-service-map__country-connector-core"
                  :cx="country.xPx"
                  :cy="country.yPx"
                  r="5"
                />
              </g>
            </svg>

            <div
              v-if="countryLabels.length"
              class="static-service-map__country-layer"
              aria-hidden="true"
            >
              <span
                v-for="country in countryLabels"
                :key="country.id"
                class="static-service-map__country-label"
                :class="{ 'static-service-map__country-label--planned': country.isComingSoon }"
                :style="{
                  left: `${country.labelX ?? country.x}%`,
                  top: `${country.labelY ?? country.y}%`,
                  transform: country.labelTransform || 'translate(-50%, -50%)'
                }"
              >
                <span class="static-service-map__country-label-core"></span>
                {{ country.label }}
              </span>
            </div>

            <div
              v-for="marker in markers"
              :key="marker.id"
              class="static-service-map__marker-item"
              :class="{ 'static-service-map__marker-item--active': activeMarkerSet.has(marker.id) }"
            >
              <button
                type="button"
                class="static-service-map__marker"
                :class="{ 'static-service-map__marker--active': activeMarkerSet.has(marker.id) }"
                :style="{
                  left: `${marker.x}%`,
                  top: `${marker.y}%`
                }"
                :aria-expanded="visibleTooltipId === marker.id ? 'true' : 'false'"
                :aria-label="marker.tooltip?.officeTitle || marker.label || marker.id"
                @pointerdown.stop
                @mouseenter="handleMarkerEnter(marker.id)"
                @mouseleave="handleMarkerLeave(marker.id)"
                @focus="handleMarkerEnter(marker.id)"
                @blur="handleMarkerLeave(marker.id)"
                @click.stop="handleMarkerClick(marker, $event)"
              >
                <span class="static-service-map__marker-pulse"></span>
                <span class="static-service-map__marker-dot"></span>
              </button>

              <button
                v-if="marker.label"
                type="button"
                class="static-service-map__label"
                :class="{ 'static-service-map__label--active': activeMarkerSet.has(marker.id) }"
                :style="{
                  left: `${marker.labelX ?? marker.x}%`,
                  top: `${marker.labelY ?? marker.y}%`,
                  transform: marker.labelTransform || 'translate(0, -50%)'
                }"
                :aria-expanded="visibleTooltipId === marker.id ? 'true' : 'false'"
                :aria-label="marker.tooltip?.officeTitle || marker.label || marker.id"
                @pointerdown.stop
                @mouseenter="handleMarkerEnter(marker.id)"
                @mouseleave="handleMarkerLeave(marker.id)"
                @focus="handleMarkerEnter(marker.id)"
                @blur="handleMarkerLeave(marker.id)"
                @click.stop="handleMarkerClick(marker, $event)"
              >
                {{ marker.label }}
              </button>

              <div
                v-if="visibleTooltipId === marker.id && marker.tooltip"
                class="static-service-map__tooltip"
                :class="getTooltipClass(marker)"
                :style="getTooltipStyle(marker)"
                role="status"
                aria-live="polite"
              >
                <div class="static-service-map__tooltip-head">
                  <div class="static-service-map__tooltip-copy">
                    <p class="static-service-map__tooltip-region">{{ marker.tooltip.regionName }}</p>
                    <h3 class="static-service-map__tooltip-title">{{ marker.tooltip.officeTitle }}</h3>
                  </div>
                  <span
                    class="static-service-map__tooltip-status"
                    :class="{
                      'static-service-map__tooltip-status--coming-soon': marker.tooltip.isComingSoon
                    }"
                  >
                    {{ marker.tooltip.statusLabel }}
                  </span>
                </div>

                <div class="static-service-map__tooltip-group static-service-map__tooltip-group--address">
                  <span class="static-service-map__tooltip-label">{{ tooltipLabels.address }}</span>
                  <div class="static-service-map__tooltip-value">
                    <span
                      v-for="line in marker.tooltip.addressLines"
                      :key="`${marker.id}-${line}`"
                      class="static-service-map__tooltip-line"
                    >
                      {{ line }}
                    </span>
                  </div>
                </div>

                <div class="static-service-map__tooltip-grid">
                  <div class="static-service-map__tooltip-group">
                    <span class="static-service-map__tooltip-label">{{ tooltipLabels.email }}</span>
                    <span class="static-service-map__tooltip-value static-service-map__tooltip-value--single">
                      {{ marker.tooltip.email }}
                    </span>
                  </div>

                  <div class="static-service-map__tooltip-group">
                    <span class="static-service-map__tooltip-label">{{ tooltipLabels.hours }}</span>
                    <span class="static-service-map__tooltip-value static-service-map__tooltip-value--single">
                      {{ marker.tooltip.businessHours }}
                    </span>
                  </div>

                  <div class="static-service-map__tooltip-group static-service-map__tooltip-group--status">
                    <span class="static-service-map__tooltip-label">{{ tooltipLabels.status }}</span>
                    <span class="static-service-map__tooltip-value static-service-map__tooltip-value--single">
                      {{ marker.tooltip.statusLabel }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { serviceMapAspectRatio, serviceMapDimensions } from '@/utils/serviceMap';

const props = defineProps({
  markers: {
    type: Array,
    default: () => []
  },
  activeMarkerIds: {
    type: Array,
    default: () => []
  },
  countryLabels: {
    type: Array,
    default: () => []
  },
  serviceArcs: {
    type: Array,
    default: () => []
  },
  presentation: {
    type: String,
    default: 'flat'
  },
  showControls: {
    type: Boolean,
    default: false
  },
  alt: {
    type: String,
    default: ''
  },
  initialZoom: {
    type: Number,
    default: 1
  },
  minZoom: {
    type: Number,
    default: 1
  },
  maxZoom: {
    type: Number,
    default: 1.45
  },
  zoomStep: {
    type: Number,
    default: 0.1
  }
});

const emit = defineEmits(['select']);
const { locale } = useI18n();

const mapViewBox = `0 0 ${serviceMapDimensions.width} ${serviceMapDimensions.height}`;
const depthGridLines = [
  'M 110 360 Q 800 230 1490 360',
  'M 82 510 Q 800 424 1518 510',
  'M 82 690 Q 800 776 1518 690',
  'M 110 840 Q 800 970 1490 840',
  'M 340 96 Q 230 600 340 1104',
  'M 565 62 Q 510 600 565 1138',
  'M 800 48 Q 800 600 800 1152',
  'M 1035 62 Q 1090 600 1035 1138',
  'M 1260 96 Q 1370 600 1260 1104'
];

const zoom = ref(props.initialZoom);
const viewportRef = ref(null);
const panX = ref(0);
const panY = ref(0);
const isDragging = ref(false);
const suppressMarkerClick = ref(false);
const hoveredMarkerId = ref(null);
const pinnedMarkerId = ref(null);

let resizeObserver;
const dragState = {
  pointerId: null,
  startX: 0,
  startY: 0,
  originPanX: 0,
  originPanY: 0,
  moved: false
};

watch(
  () => props.initialZoom,
  (value) => {
    zoom.value = value;
  }
);

watch(
  () => props.markers,
  (markers) => {
    const markerIds = new Set(markers.map(marker => marker.id));

    if (hoveredMarkerId.value && !markerIds.has(hoveredMarkerId.value)) {
      hoveredMarkerId.value = null;
    }

    if (pinnedMarkerId.value && !markerIds.has(pinnedMarkerId.value)) {
      pinnedMarkerId.value = null;
    }
  },
  { deep: false }
);

const localeKey = computed(() => {
  const [baseLocale] = (locale.value || 'zh').split('-');
  return ['zh', 'en', 'ja', 'ko'].includes(baseLocale) ? baseLocale : 'en';
});

const tooltipLabels = computed(() => {
  switch (localeKey.value) {
    case 'en':
      return {
        address: 'Office Address',
        email: 'Email',
        hours: 'Business Hours',
        status: 'Operating Status'
      };
    case 'ja':
      return {
        address: '所在地',
        email: 'メール',
        hours: '営業時間',
        status: '稼働状況'
      };
    case 'ko':
      return {
        address: '오피스 주소',
        email: '이메일',
        hours: '영업 시간',
        status: '운영 상태'
      };
    default:
      return {
        address: '办公地址',
        email: '联系邮箱',
        hours: '营业时间',
        status: '开业状态'
      };
  }
});

const visibleTooltipId = computed(() => hoveredMarkerId.value || pinnedMarkerId.value || null);
const activeMarkerSet = computed(() => {
  const ids = new Set(props.activeMarkerIds);

  if (visibleTooltipId.value) {
    ids.add(visibleTooltipId.value);
  }

  return ids;
});

const isImmersive = computed(() => props.presentation === 'immersive');
const isPannable = computed(() => zoom.value > 1.001);
const viewportStyle = computed(() => ({
  aspectRatio: serviceMapAspectRatio
}));
const panStyle = computed(() => ({
  transform: `translate3d(${panX.value}px, ${panY.value}px, 0)`,
  transition: isDragging.value ? 'none' : 'transform 0.28s ease'
}));
const sceneStyle = computed(() => ({
  transform: isImmersive.value
    ? `perspective(1600px) rotateX(7deg) rotateZ(-0.45deg) scale(${zoom.value})`
    : `scale(${zoom.value})`,
  transition: isDragging.value ? 'none' : 'transform 0.3s ease'
}));

const clampZoom = (value) => {
  return Math.min(props.maxZoom, Math.max(props.minZoom, value));
};

const clampOffset = (value, maxOffset) => {
  if (maxOffset <= 0) {
    return 0;
  }

  return Math.min(maxOffset, Math.max(-maxOffset, value));
};

const getMaxPanOffset = () => {
  const viewportWidth = viewportRef.value?.clientWidth || 0;
  const viewportHeight = viewportRef.value?.clientHeight || 0;
  const scaleDelta = Math.max(0, zoom.value - 1);

  return {
    x: (viewportWidth * scaleDelta) / 2,
    y: (viewportHeight * scaleDelta) / 2
  };
};

const syncPanWithinBounds = () => {
  const bounds = getMaxPanOffset();
  panX.value = clampOffset(panX.value, bounds.x);
  panY.value = clampOffset(panY.value, bounds.y);
};

const handleWheel = (event) => {
  const direction = event.deltaY > 0 ? -1 : 1;
  const nextZoom = clampZoom(zoom.value + direction * props.zoomStep);

  if (nextZoom === zoom.value) {
    return;
  }

  zoom.value = nextZoom;
  syncPanWithinBounds();
};

const handlePointerDown = (event) => {
  if (!isPannable.value) {
    return;
  }

  if (typeof event.button === 'number' && event.button !== 0) {
    return;
  }

  dragState.pointerId = event.pointerId;
  dragState.startX = event.clientX;
  dragState.startY = event.clientY;
  dragState.originPanX = panX.value;
  dragState.originPanY = panY.value;
  dragState.moved = false;
  suppressMarkerClick.value = false;
  hoveredMarkerId.value = null;
  isDragging.value = true;

  viewportRef.value?.setPointerCapture?.(event.pointerId);
  event.preventDefault();
};

const handlePointerMove = (event) => {
  if (!isDragging.value || dragState.pointerId !== event.pointerId) {
    return;
  }

  const deltaX = event.clientX - dragState.startX;
  const deltaY = event.clientY - dragState.startY;
  const bounds = getMaxPanOffset();

  panX.value = clampOffset(dragState.originPanX + deltaX, bounds.x);
  panY.value = clampOffset(dragState.originPanY + deltaY, bounds.y);

  if (Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6) {
    dragState.moved = true;
    suppressMarkerClick.value = true;
  }
};

const stopDragging = (pointerId) => {
  if (pointerId != null && viewportRef.value?.hasPointerCapture?.(pointerId)) {
    viewportRef.value.releasePointerCapture(pointerId);
  }

  dragState.pointerId = null;
  isDragging.value = false;
};

const handlePointerUp = (event) => {
  if (dragState.pointerId !== event.pointerId) {
    return;
  }

  stopDragging(event.pointerId);
};

const handleMarkerEnter = (markerId) => {
  hoveredMarkerId.value = markerId;
};

const handleMarkerLeave = (markerId) => {
  if (hoveredMarkerId.value === markerId) {
    hoveredMarkerId.value = null;
  }
};

const handleMarkerClick = (marker, event) => {
  if (suppressMarkerClick.value) {
    suppressMarkerClick.value = false;
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  pinnedMarkerId.value = pinnedMarkerId.value === marker.id ? null : marker.id;
  hoveredMarkerId.value = marker.id;
  emit('select', marker);
};

const handleViewportClick = () => {
  hoveredMarkerId.value = null;
  pinnedMarkerId.value = null;
};

const getTooltipClass = (marker) => {
  const classes = [];

  if (marker.x >= 72) {
    classes.push('static-service-map__tooltip--right');
  } else if (marker.x <= 24) {
    classes.push('static-service-map__tooltip--left');
  } else {
    classes.push('static-service-map__tooltip--center');
  }

  classes.push(marker.y <= 34 ? 'static-service-map__tooltip--below' : 'static-service-map__tooltip--above');

  return classes;
};

const getTooltipStyle = (marker) => ({
  left: `${marker.x}%`,
  top: `${marker.y}%`
});

const getCountryConnectorPath = (country) => {
  const startX = country.xPx ?? country.anchorXPx ?? 0;
  const startY = country.yPx ?? country.anchorYPx ?? 0;
  const endX = country.labelXPx ?? startX;
  const endY = country.labelYPx ?? startY;
  const distance = Math.hypot(endX - startX, endY - startY);
  const lift = Math.min(30, Math.max(9, distance * 0.18));
  const midX = startX + (endX - startX) * 0.55;
  const midY = Math.min(startY, endY) - lift;

  return `M ${startX} ${startY} Q ${midX} ${midY} ${endX} ${endY}`;
};

watch(zoom, () => {
  syncPanWithinBounds();
});

onMounted(() => {
  if (typeof ResizeObserver !== 'undefined' && viewportRef.value) {
    resizeObserver = new ResizeObserver(() => {
      syncPanWithinBounds();
    });
    resizeObserver.observe(viewportRef.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  stopDragging(dragState.pointerId);
});
</script>

<style scoped>
.static-service-map {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.static-service-map__frame {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
}

.static-service-map__viewport {
  position: relative;
  width: 100%;
  min-height: clamp(500px, 48vw, 860px);
  overflow: hidden;
  border-radius: 1.6rem;
  background: #ffffff;
  cursor: default;
}

.static-service-map__viewport--pannable {
  cursor: grab;
}

.static-service-map__viewport--dragging {
  cursor: grabbing;
}

.static-service-map__pan {
  position: absolute;
  inset: 0;
}

.static-service-map__scene {
  position: absolute;
  inset: 0;
  transform-origin: center center;
  will-change: transform;
}

.static-service-map__image {
  display: block;
  width: 100%;
  height: 100%;
  user-select: none;
  pointer-events: none;
  object-fit: cover;
  filter: none;
}

.static-service-map--immersive {
  padding-bottom: clamp(0.5rem, 1.5vw, 1.25rem);
}

.static-service-map--immersive .static-service-map__frame {
  perspective: 1800px;
}

.static-service-map--immersive .static-service-map__viewport {
  min-height: clamp(500px, 44vw, 800px);
  overflow: visible;
  border-radius: clamp(1.25rem, 2.4vw, 2.4rem);
  background:
    radial-gradient(circle at 16% 18%, rgba(45, 212, 191, 0.2), transparent 30%),
    radial-gradient(circle at 82% 74%, rgba(245, 158, 11, 0.16), transparent 34%),
    linear-gradient(138deg, #081728 0%, #0f2d3e 46%, #122b36 100%);
  box-shadow:
    0 36px 90px rgba(4, 17, 31, 0.22),
    inset 0 0 0 1px rgba(148, 163, 184, 0.28);
  isolation: isolate;
}

.static-service-map--immersive .static-service-map__viewport::before,
.static-service-map--immersive .static-service-map__viewport::after {
  content: '';
  position: absolute;
  pointer-events: none;
}

.static-service-map--immersive .static-service-map__viewport::before {
  left: 6%;
  right: 6%;
  bottom: -12%;
  height: 24%;
  border-radius: 9999px;
  background: radial-gradient(ellipse at center, rgba(8, 47, 73, 0.44) 0%, rgba(8, 47, 73, 0.2) 48%, transparent 72%);
  filter: blur(18px);
  transform: perspective(900px) rotateX(64deg);
  z-index: 0;
}

.static-service-map--immersive .static-service-map__viewport::after {
  inset: 5% 4% -6%;
  border-radius: clamp(1.1rem, 2vw, 2rem);
  background:
    linear-gradient(180deg, rgba(45, 212, 191, 0.18), rgba(15, 23, 42, 0.1) 46%, rgba(5, 150, 105, 0.13)),
    linear-gradient(130deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(125, 211, 252, 0.18);
  box-shadow:
    inset 0 -18px 30px rgba(5, 150, 105, 0.15),
    0 22px 54px rgba(2, 6, 23, 0.24);
  transform: perspective(1500px) rotateX(10deg) translateY(1.25rem);
  z-index: 1;
}

.static-service-map--immersive .static-service-map__pan {
  z-index: 2;
}

.static-service-map--immersive .static-service-map__scene {
  overflow: visible;
  border-radius: clamp(1.1rem, 2vw, 2rem);
  transform-style: preserve-3d;
  filter:
    drop-shadow(0 34px 42px rgba(2, 8, 23, 0.2))
    drop-shadow(0 8px 0 rgba(8, 47, 73, 0.18));
}

.static-service-map--immersive .static-service-map__scene::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background:
    linear-gradient(90deg, rgba(125, 211, 252, 0.13), transparent 20%, transparent 78%, rgba(251, 191, 36, 0.1)),
    linear-gradient(180deg, rgba(255, 255, 255, 0.12), transparent 28%, rgba(8, 47, 73, 0.18));
  box-shadow:
    inset 0 0 0 1px rgba(226, 232, 240, 0.34),
    inset 0 -28px 46px rgba(8, 47, 73, 0.16);
  pointer-events: none;
  z-index: 3;
}

.static-service-map--immersive .static-service-map__image {
  border-radius: inherit;
  filter: saturate(1.18) contrast(1.04) brightness(1.03);
}

.static-service-map__depth-grid,
.static-service-map__service-arcs,
.static-service-map__country-connectors,
.static-service-map__country-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.static-service-map__depth-grid {
  z-index: 1;
  opacity: 0.44;
}

.static-service-map__depth-grid path {
  fill: none;
  stroke: rgba(14, 116, 144, 0.36);
  stroke-width: 1.4;
  stroke-dasharray: 10 16;
  vector-effect: non-scaling-stroke;
}

.static-service-map__service-arcs {
  z-index: 2;
  filter: drop-shadow(0 0 10px rgba(45, 212, 191, 0.38));
}

.static-service-map__service-arc {
  fill: none;
  stroke: rgba(34, 211, 238, 0.88);
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-dasharray: 8 12;
  vector-effect: non-scaling-stroke;
  animation: static-map-flow 7.5s linear infinite;
}

.static-service-map__service-arc--planned {
  stroke: rgba(251, 191, 36, 0.84);
  stroke-dasharray: 3 10;
}

.static-service-map__country-connectors {
  z-index: 4;
  overflow: visible;
  filter: drop-shadow(0 8px 10px rgba(2, 8, 23, 0.16));
}

.static-service-map__country-connector path {
  fill: none;
  stroke: rgba(8, 145, 178, 0.86);
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-dasharray: 2 7;
  vector-effect: non-scaling-stroke;
}

.static-service-map__country-connector-ring {
  fill: rgba(8, 145, 178, 0.14);
  stroke: rgba(8, 145, 178, 0.78);
  stroke-width: 2.4;
  vector-effect: non-scaling-stroke;
}

.static-service-map__country-connector-core {
  fill: #0e7490;
  stroke: rgba(255, 255, 255, 0.9);
  stroke-width: 2.2;
  vector-effect: non-scaling-stroke;
}

.static-service-map__country-connector--planned path {
  stroke: rgba(202, 138, 4, 0.86);
}

.static-service-map__country-connector--planned .static-service-map__country-connector-ring {
  fill: rgba(245, 158, 11, 0.15);
  stroke: rgba(202, 138, 4, 0.82);
}

.static-service-map__country-connector--planned .static-service-map__country-connector-core {
  fill: #d97706;
}

.static-service-map__country-layer {
  z-index: 5;
}

.static-service-map__country-label {
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
  min-height: 1.9rem;
  max-width: 11.5rem;
  padding: 0.38rem 0.68rem;
  border-radius: 9999px;
  background: linear-gradient(135deg, rgba(8, 47, 73, 0.9), rgba(13, 148, 136, 0.78));
  color: #f8fafc;
  font-size: clamp(0.72rem, 0.9vw, 0.92rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: 0;
  white-space: nowrap;
  box-shadow:
    0 12px 26px rgba(2, 8, 23, 0.24),
    inset 0 0 0 1px rgba(204, 251, 241, 0.28);
  backdrop-filter: blur(10px);
}

.static-service-map__country-label-core {
  width: 0.46rem;
  height: 0.46rem;
  flex: 0 0 auto;
  border-radius: 9999px;
  background: #67e8f9;
  box-shadow: 0 0 0 0.28rem rgba(103, 232, 249, 0.16);
}

.static-service-map__country-label--planned {
  background: linear-gradient(135deg, rgba(71, 85, 105, 0.86), rgba(146, 64, 14, 0.72));
}

.static-service-map__country-label--planned .static-service-map__country-label-core {
  background: #fbbf24;
  box-shadow: 0 0 0 0.28rem rgba(251, 191, 36, 0.18);
}

.static-service-map__marker-item {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.static-service-map__marker-item--active {
  z-index: 20;
}

.static-service-map__marker {
  position: absolute;
  width: 1.32rem;
  height: 1.32rem;
  border: none;
  background: transparent;
  transform: translate(-50%, -50%);
  pointer-events: auto;
}

.static-service-map--immersive .static-service-map__marker-item {
  z-index: 7;
}

.static-service-map--immersive .static-service-map__marker-item--active {
  z-index: 32;
}

.static-service-map--immersive .static-service-map__marker {
  width: 1.45rem;
  height: 1.45rem;
}

.static-service-map--immersive .static-service-map__marker::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0.18rem;
  height: 1.8rem;
  border-radius: 9999px;
  background: linear-gradient(180deg, rgba(251, 191, 36, 0.8), rgba(20, 184, 166, 0.18), transparent);
  box-shadow: 0 0 14px rgba(34, 211, 238, 0.26);
  transform: translate(-50%, -0.05rem);
  z-index: 0;
}

.static-service-map__marker-pulse,
.static-service-map__marker-dot {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
}

.static-service-map__marker-pulse {
  opacity: 0.18;
  transform: scale(1.45);
  background: radial-gradient(circle, rgba(255, 35, 24, 0.22) 0%, rgba(255, 35, 24, 0.06) 50%, transparent 72%);
  filter: blur(1px);
  animation: static-map-node-pulse 4.8s ease-in-out infinite;
}

.static-service-map__marker-dot {
  background: #ff1b12;
  border: 4px solid #ffffff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.18);
}

.static-service-map--immersive .static-service-map__marker-pulse {
  background: radial-gradient(circle, rgba(251, 191, 36, 0.42) 0%, rgba(20, 184, 166, 0.18) 48%, transparent 74%);
  filter: blur(0);
}

.static-service-map--immersive .static-service-map__marker-dot {
  background: radial-gradient(circle at 36% 30%, #fff7ed 0%, #f97316 46%, #dc2626 100%);
  border: 3px solid rgba(255, 255, 255, 0.95);
  box-shadow:
    0 0 0 5px rgba(251, 191, 36, 0.16),
    0 12px 20px rgba(127, 29, 29, 0.22);
}

.static-service-map__marker:focus-visible,
.static-service-map__label:focus-visible {
  outline: 2px solid rgba(37, 99, 235, 0.5);
  outline-offset: 3px;
}

.static-service-map__marker--active .static-service-map__marker-pulse {
  opacity: 0.5;
}

.static-service-map__marker--active .static-service-map__marker-dot {
  box-shadow:
    0 0 0 8px rgba(255, 255, 255, 0.72),
    0 12px 24px rgba(255, 31, 31, 0.2);
}

.static-service-map__label {
  position: absolute;
  border: none;
  background: transparent;
  display: inline-block;
  padding: 0;
  color: #666666;
  font-size: 0.98rem;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: 0;
  text-align: left;
  text-shadow:
    0 0 14px rgba(255, 255, 255, 0.98),
    0 2px 12px rgba(255, 255, 255, 0.94),
    0 0 2px rgba(255, 255, 255, 1);
  white-space: nowrap;
  pointer-events: auto;
}

.static-service-map__label--active {
  color: #3f3f46;
}

.static-service-map__tooltip {
  --tooltip-x-shift: -50%;
  --tooltip-y-shift: calc(-100% - 1.35rem);
  --tooltip-arrow-left: 50%;
  --tooltip-arrow-right: auto;
  --tooltip-arrow-transform: translateX(-50%) rotate(45deg);

  position: absolute;
  width: min(23rem, calc(100vw - 3rem));
  padding: 1.05rem 1.05rem 1rem;
  border-radius: 1.1rem;
  background: linear-gradient(155deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 245, 255, 0.99) 58%, rgba(243, 232, 255, 0.98) 100%);
  box-shadow:
    0 18px 40px rgba(109, 40, 217, 0.18),
    0 8px 18px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(196, 181, 253, 0.72);
  backdrop-filter: blur(12px);
  pointer-events: none;
  z-index: 6;
  overflow: visible;
  transform: translate(var(--tooltip-x-shift), var(--tooltip-y-shift));
  animation: static-map-tooltip-fade 0.18s ease;
}

.static-service-map__tooltip::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 0.2rem;
  background: linear-gradient(90deg, #7c3aed 0%, #8b5cf6 45%, #3b82f6 100%);
  border-radius: 1.1rem 1.1rem 0 0;
}

.static-service-map__tooltip::after {
  content: '';
  position: absolute;
  bottom: -0.45rem;
  left: var(--tooltip-arrow-left);
  right: var(--tooltip-arrow-right);
  width: 0.95rem;
  height: 0.95rem;
  background: rgba(248, 242, 255, 0.98);
  border-right: 1px solid rgba(196, 181, 253, 0.72);
  border-bottom: 1px solid rgba(196, 181, 253, 0.72);
  transform: var(--tooltip-arrow-transform);
}

.static-service-map__tooltip--center {
  --tooltip-x-shift: -50%;
}

.static-service-map__tooltip--left {
  --tooltip-x-shift: 0;
  --tooltip-arrow-left: 1.4rem;
  --tooltip-arrow-transform: rotate(45deg);
}

.static-service-map__tooltip--right {
  --tooltip-x-shift: -100%;
  --tooltip-arrow-left: auto;
  --tooltip-arrow-right: 1.4rem;
  --tooltip-arrow-transform: rotate(45deg);
}

.static-service-map__tooltip--below {
  --tooltip-y-shift: 1.35rem;
}

.static-service-map__tooltip--below::after {
  top: -0.45rem;
  bottom: auto;
  border: 0;
  border-top: 1px solid rgba(196, 181, 253, 0.72);
  border-left: 1px solid rgba(196, 181, 253, 0.72);
}

.static-service-map__tooltip-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.9rem;
  margin-bottom: 0.95rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(221, 214, 254, 0.72);
}

.static-service-map__tooltip-copy {
  min-width: 0;
}

.static-service-map__tooltip-region {
  margin: 0;
  color: #7c3aed;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.static-service-map__tooltip-title {
  margin: 0.3rem 0 0;
  color: #312e81;
  font-size: 1.02rem;
  font-weight: 800;
  line-height: 1.3;
}

.static-service-map__tooltip-status {
  flex-shrink: 0;
  border-radius: 9999px;
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  color: #ffffff;
  padding: 0.42rem 0.78rem;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 10px 22px rgba(124, 58, 237, 0.22);
}

.static-service-map__tooltip-status--coming-soon {
  background: rgba(124, 58, 237, 0.1);
  color: #7c3aed;
  box-shadow: inset 0 0 0 1px rgba(167, 139, 250, 0.42);
}

.static-service-map__tooltip-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 0.8rem;
}

.static-service-map__tooltip-group {
  display: grid;
  gap: 0.28rem;
  padding: 0.7rem 0.75rem;
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: inset 0 0 0 1px rgba(233, 213, 255, 0.72);
}

.static-service-map__tooltip-group--address,
.static-service-map__tooltip-group--status {
  grid-column: 1 / -1;
}

.static-service-map__tooltip-label {
  color: #8b5cf6;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.static-service-map__tooltip-value {
  display: grid;
  gap: 0.15rem;
  color: #4c1d95;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.45;
}

.static-service-map__tooltip-value--single {
  display: block;
}

.static-service-map__tooltip-line {
  display: block;
}

.static-service-map--immersive .static-service-map__tooltip {
  width: min(22rem, calc(100vw - 3rem));
  background: linear-gradient(150deg, rgba(6, 23, 38, 0.96) 0%, rgba(13, 57, 72, 0.97) 58%, rgba(16, 78, 82, 0.96) 100%);
  border-color: rgba(125, 211, 252, 0.34);
  box-shadow:
    0 22px 50px rgba(2, 8, 23, 0.32),
    0 0 0 1px rgba(240, 249, 255, 0.06);
  z-index: 14;
}

.static-service-map--immersive .static-service-map__tooltip::before {
  background: linear-gradient(90deg, #22d3ee 0%, #14b8a6 52%, #f59e0b 100%);
}

.static-service-map--immersive .static-service-map__tooltip::after {
  background: rgba(10, 48, 64, 0.98);
  border-color: rgba(125, 211, 252, 0.34);
}

.static-service-map--immersive .static-service-map__tooltip-head {
  border-bottom-color: rgba(125, 211, 252, 0.2);
}

.static-service-map--immersive .static-service-map__tooltip-region,
.static-service-map--immersive .static-service-map__tooltip-label {
  color: #67e8f9;
}

.static-service-map--immersive .static-service-map__tooltip-title {
  color: #f8fafc;
}

.static-service-map--immersive .static-service-map__tooltip-status {
  background: linear-gradient(135deg, #0891b2 0%, #0d9488 100%);
  box-shadow: 0 10px 22px rgba(20, 184, 166, 0.2);
}

.static-service-map--immersive .static-service-map__tooltip-status--coming-soon {
  background: rgba(251, 191, 36, 0.13);
  color: #fde68a;
  box-shadow: inset 0 0 0 1px rgba(251, 191, 36, 0.34);
}

.static-service-map--immersive .static-service-map__tooltip-group {
  background: rgba(15, 23, 42, 0.28);
  box-shadow: inset 0 0 0 1px rgba(125, 211, 252, 0.16);
}

.static-service-map--immersive .static-service-map__tooltip-value {
  color: #e0f2fe;
}

@keyframes static-map-tooltip-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes static-map-flow {
  to {
    stroke-dashoffset: -120;
  }
}

@keyframes static-map-node-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.58;
  }

  50% {
    transform: scale(1.12);
    opacity: 1;
  }
}

@media (max-width: 767.98px) {
  .static-service-map__viewport {
    min-height: 400px;
    border-radius: 1.1rem;
  }

  .static-service-map--immersive .static-service-map__viewport {
    min-height: 430px;
    border-radius: 1.1rem;
  }

  .static-service-map--immersive .static-service-map__viewport::after {
    inset: 5% 4% -4%;
    transform: perspective(1200px) rotateX(8deg) translateY(0.7rem);
  }

  .static-service-map--immersive .static-service-map__scene {
    border-radius: 1rem;
  }

  .static-service-map__country-label {
    max-width: 7.5rem;
    min-height: 1.5rem;
    padding: 0.28rem 0.46rem;
    font-size: 0.66rem;
  }

  .static-service-map__country-label-core {
    width: 0.36rem;
    height: 0.36rem;
  }

  .static-service-map__label {
    font-size: 0.8rem;
  }

  .static-service-map__tooltip {
    width: min(17.5rem, calc(100vw - 2rem));
    padding: 0.85rem 0.85rem 0.8rem;
  }

  .static-service-map__tooltip-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .static-service-map__tooltip-title {
    font-size: 0.92rem;
  }

  .static-service-map__tooltip-value {
    font-size: 0.76rem;
  }
}
</style>
